/**
 * Write a summary document back to the single markdown string the backend
 * stores. Inverse of `parseSummaryDocument`: re-emitting a parsed document
 * preserves every title and body verbatim, and keeps the dialect and heading
 * levels the generator used, so editing one point never reformats the others.
 *
 * Whitespace between sections is normalised to one blank line, so the first
 * save of a loosely-spaced summary tidies it. Serializing is idempotent from
 * then on.
 */
import {
  DEFAULT_HEADING_LEVEL,
  type SummaryDocument,
  type SummaryKeyPoint,
} from './parseKeyPoints';
import { escapeBodyHeadings, stripClosingHashes } from './utils';

/** The fields a caller has to supply; the rest are derived on the next parse. */
export type SerializableKeyPoint = Pick<SummaryKeyPoint, 'title' | 'body'> &
  Partial<Pick<SummaryKeyPoint, 'level'>>;

export type SerializableSummaryDocument = Omit<SummaryDocument, 'points'> & {
  points: SerializableKeyPoint[];
};

const MAX_HEADING_LEVEL = 6;

const clampLevel = (level: number | undefined): number => {
  if (!level || !Number.isFinite(level)) return DEFAULT_HEADING_LEVEL;
  return Math.min(Math.max(Math.trunc(level), 1), MAX_HEADING_LEVEL);
};

const formatTitle = (
  point: SerializableKeyPoint,
  style: SummaryDocument['style']
): string => {
  // Trailing `#`s would be eaten as an ATX closing run on the next parse.
  const title = stripClosingHashes(point.title.trim()).trim();
  return style === 'bold'
    ? `**${title}**`
    : `${'#'.repeat(clampLevel(point.level))} ${title}`;
};

export function serializeKeyPoints(doc: SerializableSummaryDocument): string {
  const blocks = doc.points
    // A point with neither a title nor a body would serialize to a bare
    // marker and come back as an empty section — drop it instead.
    .filter((point) => point.title.trim() || point.body.trim())
    .map((point) => {
      const body = escapeBodyHeadings(point.body.trim());
      // Body but no title: emit the prose alone. A summary the generator
      // wrote without sections is edited as one titleless point, and giving
      // it a heading here would turn it into an accordion in the viewer —
      // a rendering change the reader never asked for. Titling the point is
      // how the user opts into that.
      if (!point.title.trim()) return body;
      const title = formatTitle(point, doc.style);
      return body ? `${title}\n\n${body}` : title;
    });

  const preamble = doc.preamble.trim();
  return [...(preamble ? [preamble] : []), ...blocks].join('\n\n');
}
